'use client';
// Pinned viewport-bottom WhatsApp preview bar — compact questionnaire design only
// (questionnaire_design='compact', see lib/questionnaireDesign.ts).
//
// Shows the LIVE WhatsApp message exactly as it forms from the questionnaire answers,
// via the same buildWaMessageSegments engine WaSmartButton's bubble uses — including the
// same elegant character-by-character typewriter reveal (lib/useTypewriter.ts, shared
// with WaSmartButton's own bubble) plus animated "typing…" dots for clauses still
// waiting on an unanswered question. Message CONTENT logic is untouched (lib/waMessage.ts
// is the single engine).
//
// Sending: on pro pages the send button is WaSmartButton in slim mode — which opens
// ContactModal with the waMessage (business rule #1: NEVER a direct wa.me link).
// On category pages there is no single target pro, so the button scrolls to the
// experts list, where every card's own button keeps the ContactModal flow.

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useSmartIntakeCtx } from '@/lib/smartIntakeStore';
import { useTypewriter } from '@/lib/useTypewriter';
import { buildWaMessageSegments, proFirstName } from '@/lib/waMessage';
import { fmtRating } from '@/lib/format';
import { WA_TYPING_PREVIEW_ENABLED } from '@/lib/featureFlags';
import WaIcon from './WaIcon';
import { avatarImg } from '@/lib/imgUrl';

const WaSmartButton = dynamic(() => import('./WaSmartButton'), { ssr: false });

export type CompactBarPro = {
  name: string;
  firstName?: string | null;
  phone?: string | null;
  id?: number;
  gender?: string | null;
  imgSrc: string;
  rating10: number;
  reviewCount?: number;
};

type Props = {
  categorySlug: string;
  categoryName: string | null;
  // Pro pages: the pro this page belongs to — avatar/name/rating shown above the preview,
  // send button targets them (via ContactModal). Category pages: omitted.
  pro?: CompactBarPro;
  // Category pages: name used inside the live preview text (e.g. the first listed
  // expert's first name) — same message engine, only the name differs per expert.
  previewName?: string;
};

function TypingDots() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginInlineStart: 4, verticalAlign: 'middle' }}>
      <span className="cwab-dot" />
      <span className="cwab-dot" />
      <span className="cwab-dot" />
    </span>
  );
}

// 2026-07-29 (user request, lib/featureFlags.ts): live-typing preview bar disabled
// site-wide — the gate lives in this thin wrapper (not an early-return inside the inner
// component, which would conditionally skip hooks). All code/design preserved; see
// docs/wa-typing-preview-disabled.md.
export default function CompactWaBar(props: Props) {
  if (!WA_TYPING_PREVIEW_ENABLED) return null;
  return <CompactWaBarInner {...props} />;
}

function CompactWaBarInner({ categorySlug, categoryName, pro, previewName }: Props) {
  const ctx = useSmartIntakeCtx();
  const [mounted, setMounted] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setMounted(true); }, []);

  // Reserve scroll room at the bottom equal to the bar's height so the fixed bar
  // never permanently covers the footer (same technique as ProStickyBar). Also exposes
  // the height as a --wa-bar-h CSS var (unused elsewhere currently, kept for any future
  // fixed-bottom element that needs to stack above this bar instead of overlapping it).
  useEffect(() => {
    const apply = () => {
      const h = barRef.current?.offsetHeight || 0;
      document.body.style.paddingBottom = h ? `${h}px` : '';
      document.documentElement.style.setProperty('--wa-bar-h', h ? `${h}px` : '0px');
    };
    apply();
    const ro = new ResizeObserver(apply);
    if (barRef.current) ro.observe(barRef.current);
    return () => { document.body.style.paddingBottom = ''; document.documentElement.style.removeProperty('--wa-bar-h'); ro.disconnect(); };
  }, []);

  const displayName = pro ? proFirstName(pro.name, pro.firstName) : (previewName || 'המומחה');
  const segments = buildWaMessageSegments(displayName, mounted ? ctx : null, categoryName);
  const previewText = segments.filter((s): s is { text: string } => 'text' in s).map(s => s.text).join('');
  const hasPending = segments.some(s => 'pending' in s);
  const { typed: typedPreview, isTyping } = useTypewriter(previewText);

  function scrollToExperts() {
    // Category pages have an #experts-list to scroll to; article embeds (no pro yet
    // for the category) don't — fall back to navigating to the category page itself.
    const el = document.getElementById('experts-list');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.location.href = `/${categorySlug}`;
  }

  return (
    <div ref={barRef} dir="rtl" data-ct2-hide className="fixed inset-x-0 z-40" style={{ bottom: 0, paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div style={{ background: 'rgba(255,255,255,.97)', backdropFilter: 'blur(12px)', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 20px rgba(13,71,161,.12)' }}>
        <div className="max-w-3xl mx-auto px-3 pt-2 pb-2">

          {/* Pro pages: avatar + first name + rating + category, above the message preview */}
          {pro && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <img {...avatarImg(pro.imgSrc, 38, { kind: 'pro', ctx: { category: categoryName, proName: displayName } })} width={38} height={38} loading="lazy" decoding="async"
                style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0e7ff', flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 900, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                    <span style={{ color: '#f59e0b', fontSize: 13, lineHeight: 1 }}>★</span>
                    <span style={{ fontSize: 12.5, fontWeight: 900, color: '#0d47a1' }}>{fmtRating(pro.rating10)}</span>
                    {typeof pro.reviewCount === 'number' && (
                      <span style={{ fontSize: 10.5, color: '#94a3b8' }}>({pro.reviewCount})</span>
                    )}
                  </span>
                </div>
                {categoryName && <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{categoryName}</div>}
              </div>
            </div>
          )}

          {/* Live WhatsApp message preview — same segments engine + typewriter reveal as the on-page bubble */}
          <div style={{
            background: '#dcf8c6', borderRadius: '10px 2px 10px 10px', border: '1px solid #c7ecb0',
            padding: '6px 10px', fontSize: 12, lineHeight: 1.55, color: '#111',
            whiteSpace: 'pre-wrap', maxHeight: 66, overflowY: 'auto', marginBottom: 6,
          }}>
            {typedPreview}
            {isTyping && <span className="cwab-caret" />}
            {hasPending && !isTyping && <TypingDots />}
          </div>

          {/* Send — pro pages: ContactModal flow via WaSmartButton (slim). Category pages: scroll to experts. */}
          {pro && pro.phone ? (
            <WaSmartButton proArea={null}
              proName={pro.name} proFirstName={pro.firstName || undefined} proPhone={pro.phone} proId={pro.id} proGender={pro.gender}
              categorySlug={categorySlug} categoryName={categoryName}
              slim buttonLabel={`שלח את ההודעה ל${displayName}`}
            />
          ) : (
            <button onClick={scrollToExperts}
              className="flex items-center justify-center gap-2 w-full py-2.5 px-3 rounded-xl text-sm font-bold text-white"
              style={{ background: 'var(--wa, #12864A)', border: 'none', cursor: 'pointer' }}>
              <WaIcon size={16} /> לבחירת מומחים ושליחת ההודעה 👇
            </button>
          )}
        </div>
      </div>
      <style>{`
        .cwab-dot {
          display: inline-block; width: 5px; height: 5px; border-radius: 50%;
          background: #667781; animation: cwabBounce 1.2s ease-in-out infinite;
        }
        .cwab-dot:nth-child(2) { animation-delay: .15s; }
        .cwab-dot:nth-child(3) { animation-delay: .3s; }
        @keyframes cwabBounce { 0%,60%,100% { transform: translateY(0); opacity: .5; } 30% { transform: translateY(-3px); opacity: 1; } }
        .cwab-caret {
          display: inline-block; width: 3px; height: 1.05em; border-radius: 2px;
          background: #34b7f1; vertical-align: text-bottom; margin-inline-start: 2px;
          animation: cwabCaret 1.1s ease-in-out infinite;
        }
        @keyframes cwabCaret { 0%,100% { opacity: .15 } 50% { opacity: 1 } }
      `}</style>
    </div>
  );
}
